import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createChecklistType,
  deleteChecklistType,
  fetchChecklistTypes,
  updateChecklistType,
} from "../../api/quality/checklistType";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  checklist_type: {},
};

export const checklistTypesController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteChecklistTypeAsync = createAsyncThunk(
  "checklist_types/deleteChecklistTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteChecklistType(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_checklist_type");
    }
  }
);

export const createChecklistTypeAsync = createAsyncThunk(
  "checklist_types/createChecklistTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createChecklistType(payload);
      const body = response?.data;
      if (body?.error || hasMeaningfulErrors(body?.errors)) {
        return rejectNormalized(rejectWithValue, body, "create_checklist_type");
      }
      return body;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_checklist_type");
    }
  }
);

export const updateChecklistTypeAsync = createAsyncThunk(
  "checklist_types/updateChecklistTypeAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateChecklistType(payload);
      const body = response?.data;
      if (body?.error || hasMeaningfulErrors(body?.errors)) {
        return rejectNormalized(rejectWithValue, body, "update_checklist_type");
      }
      return body;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_checklist_type");
    }
  }
);

export const getChecklistTypes = createAsyncThunk(
  "checklist_types/getChecklistTypes",
  async (controller = checklistTypesController, { rejectWithValue }) => {
    try {
      const response = await fetchChecklistTypes(controller);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "list_checklist_types");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_checklist_types");
    }
  }
);

const checklistTypeSlice = createSlice({
  name: "checklist_types",
  initialState,
  reducers: {
    resetChecklistType(state) {
      state.checklist_type = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChecklistTypes.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getChecklistTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(getChecklistTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente sjekklistetypene.");
      })
      .addCase(updateChecklistTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateChecklistTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const updated = action.payload?.data;
          if (updated?.id) {
            const index = draftState.data.findIndex(
              (type) => type.id === updated.id
            );
            if (index >= 0) {
              draftState.data[index] = updated;
            }
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateChecklistTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere sjekklistetypen.");
      })
      .addCase(createChecklistTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createChecklistTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const created = action.payload?.data;
          if (created?.id) {
            draftState.data.push(created);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createChecklistTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette sjekklistetypen.");
      })
      .addCase(deleteChecklistTypeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteChecklistTypeAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteChecklistTypeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette sjekklistetypen.");
      });
  },
});

export const { reducer } = checklistTypeSlice;

export const { resetChecklistType } = checklistTypeSlice.actions;

export default checklistTypeSlice;
